import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listUserMCPServersDataTable,
  getUserMCPFilterOptions,
} from '@kirin/api-client/api/mcp';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import type {
  PersonalMCPFilterableField as MCPFilterableField,
  PersonalMCPFilterOptionField as MCPFilterOptionField,
} from '@kirin/api-client/contracts/account-mcp';
import type { MCPSortField } from '@kirin/api-client/api/mcp';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Fetch personal MCP servers with DataTable pagination, filters, and sorting.
 */
export async function fetchPersonalMCPServers(
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<MCPServerListItem>> {
  return listUserMCPServersDataTable(
    apiClient,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as MCPSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );
}

/**
 * Fetch distinct filter option values for a personal MCP column.
 * Returns paginated result shape to support infinite-scroll in filter dropdowns.
 */
export async function fetchPersonalMCPFieldFilterOptions(
  field: MCPFilterableField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getUserMCPFilterOptions(apiClient, {
    field: field as MCPFilterOptionField,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Build the React Query key for a personal MCP filter options request.
 * Anchored to queryKeys.mcp.all so all MCP queries invalidate together.
 */
export function getPersonalMCPFilterOptionsQueryKey(
  field: MCPFilterableField,
  params: { search?: string; limit?: number }
): readonly unknown[] {
  return [
    ...queryKeys.mcp.all,
    'personal',
    'filter-options',
    field,
    ...(params.search ? [params.search] : []),
    ...(params.limit !== undefined ? [params.limit] : []),
  ] as const;
}
